import { signOnSavePrefsRepository } from './signOnSavePrefsRepository'
import { runSerializedUserSettingsOperation } from '@/shared/repositories/settings/userSettingsWriteQueue'
import {
  parseVidaasAuthPreference,
  parseVidaasSessionMode,
  type VidaasAuthPreference,
  type VidaasSessionMode,
} from '@/shared/lib/digitalSignaturePrefs'
import { isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'
import { toRepositoryError } from '@/shared/repositories/common/repositoryError'
import { getCurrentUserId, toJsonObject } from '@/shared/repositories/settings/userSettingsCommon'
import { supabase } from '@/shared/supabase/client'
import type { Json } from '@/shared/supabase/database.types'

export const userSettingsRepository = {
  ...signOnSavePrefsRepository,
  getDigitalSignaturePreferences: async (expectedUserId?: string): Promise<{
    vidaasAuthPreference: VidaasAuthPreference
    vidaasSessionMode: VidaasSessionMode
  }> => {
    if (isScreenshotFixtureMode()) {
      return { vidaasAuthPreference: 'push', vidaasSessionMode: 'each_document' }
    }
    try {
      const userId = await getCurrentUserId(expectedUserId)
      return await runSerializedUserSettingsOperation(userId, async () => {
        await getCurrentUserId(userId)
        const { data, error } = await supabase
          .from('user_settings')
          .select('chart_preferences')
          .eq('user_id', userId)
          .maybeSingle()

        if (error) {
          throw toRepositoryError(error, 'Não foi possível carregar preferências de assinatura.')
        }

        const prefs = toJsonObject(data?.chart_preferences)
        const raw = prefs.vidaasAuthPreference ?? prefs.vidaas_auth_preference
        return {
          vidaasAuthPreference: parseVidaasAuthPreference(raw),
          vidaasSessionMode: parseVidaasSessionMode(prefs.vidaasSessionMode ?? prefs.vidaas_session_mode),
        }
      })
    } catch (error) {
      throw toRepositoryError(error, 'Falha ao carregar preferências de assinatura.')
    }
  },

  updateDigitalSignaturePreferences: async (payload: {
    vidaasAuthPreference?: VidaasAuthPreference
    vidaasSessionMode?: VidaasSessionMode
  }, expectedUserId?: string): Promise<void> => {
    if (isScreenshotFixtureMode()) {
      return
    }
    try {
      const userId = await getCurrentUserId(expectedUserId)
      return await runSerializedUserSettingsOperation(userId, async () => {
        await getCurrentUserId(userId)
        const { data, error } = await supabase
          .from('user_settings')
          .select('chart_preferences')
          .eq('user_id', userId)
          .maybeSingle()

        if (error) {
          throw toRepositoryError(error, 'Não foi possível carregar preferências atuais.')
        }

        const currentPreferences = toJsonObject(data?.chart_preferences)
        const mergedPreferences: Json = {
          ...currentPreferences,
          ...(payload.vidaasAuthPreference
            ? { vidaasAuthPreference: payload.vidaasAuthPreference }
            : {}),
          ...(payload.vidaasSessionMode ? { vidaasSessionMode: payload.vidaasSessionMode } : {}),
        }

        await getCurrentUserId(userId)
        const { error: saveError } = await supabase
          .from('user_settings')
          .upsert(
            {
              user_id: userId,
              chart_preferences: mergedPreferences,
            },
            { onConflict: 'user_id' },
          )

        if (saveError) {
          throw toRepositoryError(saveError, 'Não foi possível salvar preferências de assinatura.')
        }
      })
    } catch (error) {
      throw toRepositoryError(error, 'Falha ao salvar preferências de assinatura.')
    }
  },

  /** Preferência de mascarar valores em R$ de plantões (Home + página Plantões). Sem preferência salva → visível. */
  getPlantoesValuesMasked: async (): Promise<boolean> => {
    try {
      const userId = await getCurrentUserId()
      return await runSerializedUserSettingsOperation(userId, async () => {
        const { data, error } = await supabase
          .from('user_settings')
          .select('chart_preferences')
          .eq('user_id', userId)
          .maybeSingle()

        if (error) return false
        const prefs = toJsonObject(data?.chart_preferences)
        return prefs.plantoesValuesMasked === true
      })
    } catch {
      return false
    }
  },

  updatePlantoesValuesMasked: async (masked: boolean): Promise<void> => {
    try {
      const userId = await getCurrentUserId()
      return await runSerializedUserSettingsOperation(userId, async () => {
        const { data, error } = await supabase
          .from('user_settings')
          .select('chart_preferences')
          .eq('user_id', userId)
          .maybeSingle()

        if (error) {
          throw toRepositoryError(error, 'Não foi possível carregar preferências atuais.')
        }

        const currentPreferences = toJsonObject(data?.chart_preferences)
        const mergedPreferences: Json = {
          ...currentPreferences,
          plantoesValuesMasked: masked,
        }

        const { error: saveError } = await supabase
          .from('user_settings')
          .upsert(
            { user_id: userId, chart_preferences: mergedPreferences },
            { onConflict: 'user_id' },
          )
        if (saveError) {
          throw toRepositoryError(saveError, 'Não foi possível salvar preferência de máscara de valores.')
        }
      })
    } catch (error) {
      throw toRepositoryError(error, 'Falha ao salvar preferência de máscara de valores.')
    }
  },
}
